import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type Employee from '@src/Objects/Employee';
import { Award, CheckCircle } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ProfileCertificationsSectionProps {
    employee: Employee;
}

function ProfileCertificationsSection({ employee }: ProfileCertificationsSectionProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    // TODO: Check if certificates from onboarding process (CV analysis) are properly mapped to employee.certificates
    // Onboarding certificates might be stored in EmployeeTalk.talkState instead of employee.certificates
    // Need to merge both data sources or fix the mapping from onboarding to employee certificates
    const certificates = [...(employee.certificates || [])].sort((a, b) => a.title.localeCompare(b.title)).slice(0, 4);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Zertifikate & Qualifikationen
            </Typography>

            {certificates.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Noch keine Zertifikate vorhanden
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {certificates.map((certificate) => (
                        <Paper
                            key={certificate.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: theme.palette.success.main,
                                    bgcolor: alpha(theme.palette.success.main, 0.05),
                                    transform: 'translateX(4px)',
                                },
                            }}
                            onClick={() => navigate(`/${ELinks.CERTIFICATES}/${certificate.id}`)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Award size={24} color={theme.palette.success.main} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {certificate.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {certificate.startDate && (
                                            <Chip
                                                label={new Date(certificate.startDate).toLocaleDateString('de-DE', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                })}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                                    color: theme.palette.info.main,
                                                }}
                                            />
                                        )}
                                        {certificate.endDate ? (
                                            <Chip
                                                icon={
                                                    new Date(certificate.endDate) < new Date() ? undefined : (
                                                        <CheckCircle size={12} />
                                                    )
                                                }
                                                label={
                                                    new Date(certificate.endDate) < new Date() ? 'Abgelaufen' : 'Aktiv'
                                                }
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    bgcolor: alpha(
                                                        new Date(certificate.endDate) < new Date()
                                                            ? theme.palette.error.main
                                                            : theme.palette.success.main,
                                                        0.1,
                                                    ),
                                                    color:
                                                        new Date(certificate.endDate) < new Date()
                                                            ? theme.palette.error.main
                                                            : theme.palette.success.main,
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                icon={<CheckCircle size={12} />}
                                                label="Läuft nicht ab"
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                                    color: theme.palette.success.main,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

export default ProfileCertificationsSection;
